// packages/frontend/src/components/auth/LogoutDialog.tsx

import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Coffee, Rocket, Cookie } from "lucide-react";
import { createLogger } from "../../utils/logger";

const logger = createLogger("LogoutDialog");

// Global state for logout dialog
let logoutDialogState: {
  show: (onAcknowledge: () => void) => void;
  hide: () => void;
} | null = null;

/**
 * Set the global logout dialog state manager
 * Called by the LogoutDialogProvider component
 */
export function setLogoutDialogManager(manager: typeof logoutDialogState) {
  logoutDialogState = manager;
}

/**
 * Show logout dialog from non-React contexts
 * Returns a promise that resolves when user acknowledges
 */
export function showLogoutDialog(): Promise<void> {
  return new Promise((resolve) => {
    if (!logoutDialogState) {
      logger.warn(
        "LogoutDialog not initialized, proceeding with logout immediately",
      );
      resolve();
      return;
    }

    logoutDialogState.show(() => {
      resolve();
    });
  });
}

/**
 * Dismiss the dialog and release every check parked on it (see
 * setLogoutDialogManager below), without treating the dismissal as a user
 * acknowledgement. Used when a later check refreshes the token successfully
 * while an earlier check's dialog is still open (formal finding 7): that
 * earlier check must not log the user out on stale information.
 */
export function hideLogoutDialog(): void {
  logoutDialogState?.hide();
}

/**
 * LogoutDialog Component
 * Displays a blocking modal dialog when session expires
 */
export function LogoutDialog() {
  const { t } = useTranslation(["common"]);
  const [isOpen, setIsOpen] = useState(false);
  // Every parked check that showed this dialog gets its own callback here.
  // One user acknowledgement (or a programmatic retraction) releases all of
  // them, so a second `show()` while the dialog is already open can no
  // longer strand the first caller (formal finding 7, NoStrandedAwaiter).
  const [acknowledgers, setAcknowledgers] = useState<Array<() => void>>([]);

  // Random humorous messages with styling
  const messages = useMemo(
    () => [
      {
        icon: Coffee,
        message: t("common:logoutNotification.messages.coffee"),
        color: "text-amber-600",
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200",
      },
      {
        icon: Rocket,
        message: t("common:logoutNotification.messages.adventure"),
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200",
      },
      {
        icon: Cookie,
        message: t("common:logoutNotification.messages.snack"),
        color: "text-orange-600",
        bgColor: "bg-orange-50",
        borderColor: "border-orange-200",
      },
    ],
    [t],
  );

  const randomMessage = useMemo(() => {
    return messages[Math.floor(Math.random() * messages.length)];
  }, [messages]);

  const Icon = randomMessage.icon;

  // Set up global manager on mount
  useEffect(() => {
    setLogoutDialogManager({
      show: (acknowledgeCallback) => {
        setAcknowledgers((prev) => [...prev, acknowledgeCallback]);
        setIsOpen(true);
      },
      hide: () => {
        // Retraction (e.g. a later check refreshed the token successfully):
        // release every parked check, not just the most recent one.
        setAcknowledgers((prev) => {
          prev.forEach((cb) => cb());
          return [];
        });
        setIsOpen(false);
      },
    });

    return () => {
      setLogoutDialogManager(null);
    };
  }, []);

  const handleAcknowledge = useCallback(() => {
    acknowledgers.forEach((cb) => cb());
    setIsOpen(false);
    setAcknowledgers([]);
  }, [acknowledgers]);

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={(open) => {
        // Prevent closing by clicking outside or pressing Escape
        // User must click the acknowledge button
        // This handler is called when user tries to close, but we ignore it
        if (!open) {
          // Force dialog to stay open - user must acknowledge
          // We don't update state, so dialog remains open
          return;
        }
      }}
    >
      <AlertDialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => {
          // Prevent Escape key from closing
          e.preventDefault();
        }}
      >
        <AlertDialogHeader className="flex flex-col items-center text-center">
          <div
            className={`rounded-full p-3 mb-4 border-2 ${randomMessage.bgColor} ${randomMessage.borderColor}`}
          >
            <Icon className={`h-8 w-8 ${randomMessage.color}`} />
          </div>
          <AlertDialogTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {t("common:logoutNotification.title")}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-base text-gray-600 dark:text-gray-300 mt-2 max-w-sm mx-auto">
            {randomMessage.message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={handleAcknowledge}
            className="w-full sm:w-auto"
            autoFocus
          >
            {t("common:logoutNotification.acknowledge")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
